import { computed, defineComponent, h } from 'vue';
import type { DefineComponent, PropType } from 'vue';

import type { FileDiffOptions } from '../components/FileDiff';
import type {
  DiffLineAnnotation,
  FileContents,
  SelectedLineRange,
  VirtualFileMetrics,
} from '../types';
import { parseDiffFromFile } from '../utils/parseDiffFromFile';
import { FileDiff } from './FileDiff';
import type { MultiFileDiffPropsVue } from './types';

// Compares two file versions by parsing them into a `FileDiffMetadata`, then
// delegates rendering (and all scoped slots) to `FileDiff`.
const MultiFileDiffImpl = defineComponent({
  name: 'PierreMultiFileDiff',
  props: {
    oldFile: { type: Object as PropType<FileContents>, required: true },
    newFile: { type: Object as PropType<FileContents>, required: true },
    options: {
      type: Object as PropType<FileDiffOptions<undefined>>,
      default: undefined,
    },
    metrics: {
      type: Object as PropType<VirtualFileMetrics>,
      default: undefined,
    },
    lineAnnotations: {
      type: Array as PropType<DiffLineAnnotation[]>,
      default: undefined,
    },
    selectedLines: {
      type: Object as PropType<SelectedLineRange | null>,
      default: undefined,
    },
    prerenderedHTML: { type: String, default: undefined },
    disableWorkerPool: { type: Boolean, default: false },
  },
  setup(props, { slots }) {
    const fileDiff = computed(() =>
      parseDiffFromFile(
        props.oldFile,
        props.newFile,
        props.options?.parseDiffOptions
      )
    );
    return () =>
      h(
        FileDiff,
        {
          fileDiff: fileDiff.value,
          options: props.options,
          metrics: props.metrics,
          lineAnnotations: props.lineAnnotations,
          selectedLines: props.selectedLines,
          prerenderedHTML: props.prerenderedHTML,
          disableWorkerPool: props.disableWorkerPool,
        },
        slots
      );
  },
});

// See the note on `FileDiff` for why this explicit annotation/cast is required.
export const MultiFileDiff =
  MultiFileDiffImpl as unknown as DefineComponent<MultiFileDiffPropsVue>;
